import { useFeaturesStore } from '#stores/useFeaturesStore.js'
import { useModulesStore } from '#stores/useModulesStore.js'
import { usePermissionsStore } from '#stores/usePermissionsStore.js'
import { useSettingsStore } from '#stores/useSettingsStore.js'

const registrationRoutes = {
	path: '/registrations',
	name: 'modules.registrations',
	meta: { requiresAuth: true, permissions: ['registrations'] },
	redirect: () => {
		const settings = useSettingsStore()
		const permissions = usePermissionsStore()
		const features = useFeaturesStore()
		const modules = useModulesStore()

		// If registration module is available, redirect to registrations/registrations
		if (
			settings.get('registrations', 'tenant_uses_registrations') === '1' &&
			permissions.has('registrations.registration.registration')
		) {
			return { name: 'registrations.registrations.index' }
		}

		// If inspections module is available, also redirect to registrations/registrations for now
		if (
			settings.get('registrations', 'tenant_uses_inspections') === '1' &&
			permissions.has('registrations.registration.inspection')
		) {
			return { name: 'registrations.registrations.index' }
		}

		// Else, just use the first available item in the array
		if (permissions.has('registrations.anomalities.anomalities'))
			return { name: 'registrations.deviations.index' }
		if (
			permissions.has('registrations.actions.actions') &&
			!(features.isActive('enable_actions_module') && modules.isEnabled('actions'))
		)
			return { name: 'registrations.actions.index' }
		if (permissions.has('registrations.plans.plans'))
			return { name: 'registrations.planning.index' }
		if (permissions.has('registrations.statistics.statistics'))
			return { name: 'registrations.statistics.index' }
		if (permissions.has('registrations.questionnaire.questionnaire'))
			return { name: 'registrations.manage.index' }
		return { name: 'app.home' }
	},
	component: () => import('#modules/registrations/RegistrationsModule.vue'),
	children: [
		{
			path: 'registrations',
			name: 'registrations.registrations',
			redirect: { name: 'registrations.registrations.index' },
			meta: {
				settingsAny: [
					{ module: 'registrations', key: 'tenant_uses_registrations' },
					{ module: 'registrations', key: 'tenant_uses_inspections' },
				],
			},
			children: [
				{
					path: '',
					name: 'registrations.registrations.index',
					component: () => import('#modules/registrations/pages/RegistrationsIndex.vue'),
					meta: {
						permissionsAny: [
							'registrations.registration.registration',
							'registrations.registration.inspection',
						],
					},
				},
				{
					path: ':registrationId(\\d+)',
					name: 'registrations.registrations.show',
					component: () => import('#modules/registrations/pages/RegistrationPage.vue'),
					props: { isNew: false, isEdit: false },
					meta: { permissions: ['registrations'] },
				},
				{
					path: ':registrationId(\\d+)/edit',
					name: 'registrations.registrations.edit',
					component: () => import('#modules/registrations/pages/RegistrationPage.vue'),
					props: { isNew: false, isEdit: true },
					meta: { permissions: ['registrations'] },
				},
			],
		},
		{
			path: 'deviations',
			name: 'registrations.deviations',
			redirect: { name: 'registrations.deviations.index' },
			children: [
				{
					path: '',
					name: 'registrations.deviations.index',
					component: () => import('#modules/registrations/pages/DeviationsIndex.vue'),
				},
				{
					path: ':deviationId(\\d+)',
					name: 'registrations.deviations.show',
					component: () => import('#modules/registrations/pages/DeviationPage.vue'),
					props: { isEdit: false },
				},
				{
					path: ':deviationId(\\d+)/edit',
					name: 'registrations.deviations.edit',
					component: () => import('#modules/registrations/pages/DeviationPage.vue'),
					props: { isEdit: true },
				},
			],
		},
		{
			path: 'actions',
			name: 'registrations.actions',
			redirect: { name: 'registrations.actions.index' },
			meta: { featureDisabled: 'enable_actions_module' },
			children: [
				{
					path: '',
					name: 'registrations.actions.index',
					component: () => import('#modules/registrations/pages/ActionsIndex.vue'),
				},
				{
					path: ':actionId(\\d+)',
					name: 'registrations.actions.show',
					component: () => import('#modules/registrations/pages/ActionPage.vue'),
					props: { isEdit: false },
				},
				{
					path: ':actionId(\\d+)/edit',
					name: 'registrations.actions.edit',
					component: () => import('#modules/registrations/pages/ActionPage.vue'),
					props: { isEdit: true },
				},
			],
		},
		{
			path: 'planning',
			name: 'registrations.planning',
			redirect: { name: 'registrations.planning.index' },
			children: [
				{
					path: '',
					name: 'registrations.planning.index',
					component: () => import('#modules/registrations/pages/PlanningIndex.vue'),
				},
				{
					path: 'add',
					name: 'registrations.planning.create',
					component: () => import('#modules/registrations/pages/PlannedQuestionnaire.vue'),
					meta: { permissions: ['registrations.plans.plans.create'] },
					props: { isNew: true, isEdit: true },
				},
				{
					path: ':plannedQuestionnaireId(\\d+)',
					name: 'registrations.planning.show',
					component: () => import('#modules/registrations/pages/PlannedQuestionnaire.vue'),
					props: { isNew: false, isEdit: false },
				},
				{
					path: ':plannedQuestionnaireId(\\d+)/edit',
					name: 'registrations.planning.edit',
					component: () => import('#modules/registrations/pages/PlannedQuestionnaire.vue'),
					props: { isNew: false, isEdit: true },
				},
			],
		},
		{
			name: 'registrations.statistics.index',
			path: 'statistics',
			meta: { permissions: ['registrations.statistics.statistics'] },
			component: () => import('#modules/registrations/pages/StatisticsIndex.vue'),
		},
		{
			path: 'questionnaires',
			name: 'registrations.manage',
			redirect: { name: 'registrations.manage.index' },
			children: [
				{
					path: '',
					name: 'registrations.manage.index',
					component: () => import('#modules/registrations/pages/QuestionnairesIndex.vue'),
					meta: { permissions: ['registrations.questionnaire.questionnaire'] },
				},
				{
					path: 'create',
					name: 'registrations.questionnaires.create',
					component: () => import('#modules/registrations/pages/QuestionnairePage.vue'),
					props: { isNew: true, isEdit: true },
				},
				{
					path: ':questionnaireId(\\d+)',
					name: 'registrations.questionnaires.show',
					component: () => import('#modules/registrations/pages/QuestionnairePage.vue'),
					props: { isNew: false, isEdit: false },
				},
				{
					path: ':questionnaireId(\\d+)/edit',
					name: 'registrations.questionnaires.edit',
					component: () => import('#modules/registrations/pages/QuestionnairePage.vue'),
					props: { isNew: false, isEdit: true },
				},
				{
					path: 'versions/:questionnaireVersionId(\\d+)',
					name: 'registrations.questionnaires.versions.show',
					component: () => import('#modules/registrations/pages/QuestionnaireVersion.vue'),
					props: { isNew: false, isEdit: false },
				},
			],
		},
	],
}
export default registrationRoutes
